/**
 * Frame template data
 * 50+ frame definitions organized by category
 */

export interface FrameData {
  id: string;
  name: string;
  color: string;
  category: FrameCategory;
  material: 'wood' | 'metal' | 'plastic' | 'composite';
  profile: 'flat' | 'rounded' | 'stepped' | 'ornate' | 'floater';
  defaultWidth: number; // mm
  isPremium?: boolean;
  /** Texture type for canvas rendering */
  texture?: 'wood-grain' | 'brushed-metal' | 'smooth' | 'distressed' | 'gilded';
  /** Secondary color for gradient/grain */
  secondaryColor?: string;
}

export type FrameCategory =
  | 'classic'
  | 'modern'
  | 'ornate'
  | 'floater'
  | 'minimal'
  | 'wooden'
  | 'metal'
  | 'round';

/** Frame categories for the selector UI */
export const FRAME_CATEGORIES: { id: FrameCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'classic', label: 'Classic' },
  { id: 'modern', label: 'Modern' },
  { id: 'wooden', label: 'Wooden' },
  { id: 'metal', label: 'Metal' },
  { id: 'ornate', label: 'Ornate' },
  { id: 'floater', label: 'Floater' },
  { id: 'minimal', label: 'Minimal' },
];

/** All frame definitions */
export const FRAMES: FrameData[] = [
  // === Classic ===
  {
    id: 'frame-001',
    name: 'Classic Black',
    color: '#1a1a1a',
    category: 'classic',
    material: 'wood',
    profile: 'stepped',
    defaultWidth: 30,
    texture: 'smooth',
  },
  {
    id: 'frame-002',
    name: 'Natural Oak',
    color: '#b8956e',
    category: 'classic',
    material: 'wood',
    profile: 'rounded',
    defaultWidth: 30,
    texture: 'wood-grain',
    secondaryColor: '#a07850',
  },
  {
    id: 'frame-007',
    name: 'Walnut',
    color: '#5c4033',
    category: 'classic',
    material: 'wood',
    profile: 'rounded',
    defaultWidth: 30,
    texture: 'wood-grain',
    secondaryColor: '#4a3328',
  },
  {
    id: 'frame-008',
    name: 'Espresso',
    color: '#3c2415',
    category: 'classic',
    material: 'wood',
    profile: 'stepped',
    defaultWidth: 28,
    texture: 'wood-grain',
    secondaryColor: '#2e1a0e',
  },
  {
    id: 'frame-013',
    name: 'Mahogany',
    color: '#4e1609',
    category: 'classic',
    material: 'wood',
    profile: 'stepped',
    defaultWidth: 32,
    texture: 'wood-grain',
    secondaryColor: '#6b2214',
  },
  {
    id: 'frame-014',
    name: 'Cherry',
    color: '#7b3f00',
    category: 'classic',
    material: 'wood',
    profile: 'rounded',
    defaultWidth: 28,
    texture: 'wood-grain',
    secondaryColor: '#993d00',
  },
  {
    id: 'frame-015',
    name: 'Maple',
    color: '#c4a35a',
    category: 'classic',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 25,
    texture: 'wood-grain',
    secondaryColor: '#b89040',
  },
  {
    id: 'frame-016',
    name: 'Dark Walnut',
    color: '#3b2820',
    category: 'classic',
    material: 'wood',
    profile: 'stepped',
    defaultWidth: 30,
    texture: 'wood-grain',
    secondaryColor: '#2a1d18',
  },
  {
    id: 'frame-017',
    name: 'Driftwood',
    color: '#8b8378',
    category: 'classic',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 35,
    texture: 'distressed',
    secondaryColor: '#706860',
  },

  // === Modern ===
  {
    id: 'frame-003',
    name: 'White Modern',
    color: '#f5f5f5',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 20,
    texture: 'smooth',
  },
  {
    id: 'frame-009',
    name: 'Champagne',
    color: '#f7e7ce',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 22,
    texture: 'smooth',
  },
  {
    id: 'frame-018',
    name: 'Matte White',
    color: '#ffffff',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 18,
    texture: 'smooth',
  },
  {
    id: 'frame-019',
    name: 'Warm Grey',
    color: '#9e9e9e',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 20,
    texture: 'smooth',
  },
  {
    id: 'frame-020',
    name: 'Cool Grey',
    color: '#78909c',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 20,
    texture: 'smooth',
  },
  {
    id: 'frame-021',
    name: 'Taupe',
    color: '#b8a99a',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 22,
    texture: 'smooth',
  },
  {
    id: 'frame-022',
    name: 'Sand',
    color: '#d2b48c',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 22,
    texture: 'smooth',
  },
  {
    id: 'frame-023',
    name: 'Blush',
    color: '#de98a0',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 18,
    texture: 'smooth',
    isPremium: true,
  },
  {
    id: 'frame-024',
    name: 'Navy Modern',
    color: '#2c3e50',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 20,
    texture: 'smooth',
  },

  // === Wooden ===
  {
    id: 'frame-025',
    name: 'Pine Natural',
    color: '#deb887',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 25,
    texture: 'wood-grain',
    secondaryColor: '#c8a06e',
  },
  {
    id: 'frame-026',
    name: 'Ash Blonde',
    color: '#e8d5b7',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 22,
    texture: 'wood-grain',
    secondaryColor: '#d4c0a0',
  },
  {
    id: 'frame-027',
    name: 'Birch',
    color: '#f5e6cb',
    category: 'wooden',
    material: 'wood',
    profile: 'rounded',
    defaultWidth: 25,
    texture: 'wood-grain',
    secondaryColor: '#e8d4b2',
  },
  {
    id: 'frame-028',
    name: 'Teak',
    color: '#876e45',
    category: 'wooden',
    material: 'wood',
    profile: 'stepped',
    defaultWidth: 30,
    texture: 'wood-grain',
    secondaryColor: '#6e5a38',
  },
  {
    id: 'frame-029',
    name: 'Bamboo',
    color: '#c9b06b',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 20,
    texture: 'wood-grain',
    secondaryColor: '#b59d58',
  },
  {
    id: 'frame-030',
    name: 'Reclaimed Wood',
    color: '#7d6b5d',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 40,
    texture: 'distressed',
    secondaryColor: '#5a4e42',
  },
  {
    id: 'frame-031',
    name: 'Whitewash',
    color: '#e8e0d8',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 30,
    texture: 'distressed',
    secondaryColor: '#d0c8c0',
  },
  {
    id: 'frame-032',
    name: 'Ebony',
    color: '#1c1c1c',
    category: 'wooden',
    material: 'wood',
    profile: 'stepped',
    defaultWidth: 28,
    texture: 'wood-grain',
    secondaryColor: '#0e0e0e',
  },

  // === Wooden Thin ===
  {
    id: 'frame-065',
    name: 'Slim Oak',
    color: '#c4a06a',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'wood-grain',
    secondaryColor: '#b08e55',
  },
  {
    id: 'frame-066',
    name: 'Slim Walnut',
    color: '#5c4033',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'wood-grain',
    secondaryColor: '#4a3328',
  },
  {
    id: 'frame-067',
    name: 'Slim Ash',
    color: '#e0d2b8',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 8,
    texture: 'wood-grain',
    secondaryColor: '#cec0a5',
  },
  {
    id: 'frame-068',
    name: 'Slim Cherry',
    color: '#7b3f00',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'wood-grain',
    secondaryColor: '#633200',
  },
  {
    id: 'frame-069',
    name: 'Slim Pine',
    color: '#d4a960',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 12,
    texture: 'wood-grain',
    secondaryColor: '#c09548',
  },
  {
    id: 'frame-070',
    name: 'Slim Birch',
    color: '#f0e2c8',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 8,
    texture: 'wood-grain',
    secondaryColor: '#e0d0b0',
  },
  {
    id: 'frame-071',
    name: 'Slim Wenge',
    color: '#3c3028',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'wood-grain',
    secondaryColor: '#2a2018',
  },
  {
    id: 'frame-072',
    name: 'Slim Maple',
    color: '#d4b87a',
    category: 'wooden',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 12,
    texture: 'wood-grain',
    secondaryColor: '#c0a468',
  },

  // === Metal ===
  {
    id: 'frame-005',
    name: 'Silver Float',
    color: '#c0c0c0',
    category: 'metal',
    material: 'metal',
    profile: 'floater',
    defaultWidth: 15,
    texture: 'brushed-metal',
    secondaryColor: '#a8a8a8',
  },
  {
    id: 'frame-033',
    name: 'Brushed Silver',
    color: '#c0c0c0',
    category: 'metal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 12,
    texture: 'brushed-metal',
    secondaryColor: '#a0a0a0',
  },
  {
    id: 'frame-034',
    name: 'Matte Black Metal',
    color: '#2a2a2a',
    category: 'metal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 12,
    texture: 'brushed-metal',
    secondaryColor: '#1a1a1a',
  },
  {
    id: 'frame-035',
    name: 'Copper',
    color: '#b87333',
    category: 'metal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 15,
    texture: 'brushed-metal',
    secondaryColor: '#a06020',
    isPremium: true,
  },
  {
    id: 'frame-036',
    name: 'Pewter',
    color: '#8a8a8a',
    category: 'metal',
    material: 'metal',
    profile: 'rounded',
    defaultWidth: 15,
    texture: 'brushed-metal',
    secondaryColor: '#707070',
  },
  {
    id: 'frame-037',
    name: 'Brass',
    color: '#b5a642',
    category: 'metal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 12,
    texture: 'brushed-metal',
    secondaryColor: '#9a8e38',
    isPremium: true,
  },
  {
    id: 'frame-038',
    name: 'Gun Metal',
    color: '#505050',
    category: 'metal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'brushed-metal',
    secondaryColor: '#3a3a3a',
  },
  {
    id: 'frame-039',
    name: 'Chrome',
    color: '#e0e0e0',
    category: 'metal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'brushed-metal',
    secondaryColor: '#cccccc',
  },

  // === Ornate ===
  {
    id: 'frame-004',
    name: 'Gold Ornate',
    color: '#c9a227',
    category: 'ornate',
    material: 'wood',
    profile: 'ornate',
    defaultWidth: 45,
    texture: 'gilded',
    secondaryColor: '#e6c948',
    isPremium: true,
  },
  {
    id: 'frame-010',
    name: 'Rose Gold',
    color: '#b76e79',
    category: 'ornate',
    material: 'metal',
    profile: 'ornate',
    defaultWidth: 35,
    texture: 'gilded',
    secondaryColor: '#d4919c',
    isPremium: true,
  },
  {
    id: 'frame-011',
    name: 'Antique Bronze',
    color: '#665d1e',
    category: 'ornate',
    material: 'wood',
    profile: 'ornate',
    defaultWidth: 40,
    texture: 'gilded',
    secondaryColor: '#8a7f30',
    isPremium: true,
  },
  {
    id: 'frame-040',
    name: 'Antique Gold',
    color: '#b89b4c',
    category: 'ornate',
    material: 'wood',
    profile: 'ornate',
    defaultWidth: 50,
    texture: 'gilded',
    secondaryColor: '#d4b768',
    isPremium: true,
  },
  {
    id: 'frame-041',
    name: 'Baroque Silver',
    color: '#a8a8a8',
    category: 'ornate',
    material: 'wood',
    profile: 'ornate',
    defaultWidth: 55,
    texture: 'gilded',
    secondaryColor: '#c8c8c8',
    isPremium: true,
  },
  {
    id: 'frame-042',
    name: 'Rococo White',
    color: '#f0ebe0',
    category: 'ornate',
    material: 'wood',
    profile: 'ornate',
    defaultWidth: 50,
    texture: 'gilded',
    secondaryColor: '#ffffff',
    isPremium: true,
  },
  {
    id: 'frame-043',
    name: 'Venetian',
    color: '#8b6914',
    category: 'ornate',
    material: 'wood',
    profile: 'ornate',
    defaultWidth: 45,
    texture: 'gilded',
    secondaryColor: '#b08a20',
    isPremium: true,
  },
  {
    id: 'frame-044',
    name: 'Art Nouveau',
    color: '#6b4423',
    category: 'ornate',
    material: 'wood',
    profile: 'ornate',
    defaultWidth: 40,
    texture: 'gilded',
    secondaryColor: '#8b6040',
    isPremium: true,
  },

  // === Floater ===
  {
    id: 'frame-012',
    name: 'Canvas Float',
    color: '#e8e0d0',
    category: 'floater',
    material: 'wood',
    profile: 'floater',
    defaultWidth: 15,
    texture: 'smooth',
  },
  {
    id: 'frame-045',
    name: 'Black Float',
    color: '#1a1a1a',
    category: 'floater',
    material: 'wood',
    profile: 'floater',
    defaultWidth: 15,
    texture: 'smooth',
  },
  {
    id: 'frame-046',
    name: 'Natural Float',
    color: '#c4a35a',
    category: 'floater',
    material: 'wood',
    profile: 'floater',
    defaultWidth: 15,
    texture: 'wood-grain',
    secondaryColor: '#b09040',
  },
  {
    id: 'frame-047',
    name: 'White Float',
    color: '#ffffff',
    category: 'floater',
    material: 'composite',
    profile: 'floater',
    defaultWidth: 12,
    texture: 'smooth',
  },
  {
    id: 'frame-048',
    name: 'Gold Float',
    color: '#c9a227',
    category: 'floater',
    material: 'metal',
    profile: 'floater',
    defaultWidth: 12,
    texture: 'brushed-metal',
    secondaryColor: '#b89020',
    isPremium: true,
  },

  // === Modern Thin ===
  {
    id: 'frame-055',
    name: 'Slim Charcoal',
    color: '#3a3a3a',
    category: 'modern',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 8,
    texture: 'smooth',
  },
  {
    id: 'frame-056',
    name: 'Slim Sage',
    color: '#8fa387',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'smooth',
  },
  {
    id: 'frame-057',
    name: 'Slim Terracotta',
    color: '#c67b5c',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'smooth',
  },
  {
    id: 'frame-058',
    name: 'Slim Midnight',
    color: '#1a1f3a',
    category: 'modern',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 8,
    texture: 'brushed-metal',
    secondaryColor: '#12162e',
  },
  {
    id: 'frame-059',
    name: 'Slim Cream',
    color: '#f2ece0',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'smooth',
  },
  {
    id: 'frame-060',
    name: 'Slim Olive',
    color: '#5c6b4f',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'smooth',
  },
  {
    id: 'frame-061',
    name: 'Slim Dusty Rose',
    color: '#c9a0a0',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 8,
    texture: 'smooth',
    isPremium: true,
  },
  {
    id: 'frame-062',
    name: 'Slim Slate',
    color: '#5a6472',
    category: 'modern',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 6,
    texture: 'brushed-metal',
    secondaryColor: '#4a5462',
  },
  {
    id: 'frame-063',
    name: 'Slim Oat',
    color: '#d9cdb8',
    category: 'modern',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 12,
    texture: 'smooth',
  },
  {
    id: 'frame-064',
    name: 'Slim Carbon',
    color: '#2c2c2c',
    category: 'modern',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 6,
    texture: 'brushed-metal',
    secondaryColor: '#1e1e1e',
  },

  // === Minimal ===
  {
    id: 'frame-006',
    name: 'Thin Black',
    color: '#333333',
    category: 'minimal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 8,
    texture: 'smooth',
  },
  {
    id: 'frame-049',
    name: 'Thin White',
    color: '#ffffff',
    category: 'minimal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 8,
    texture: 'smooth',
  },
  {
    id: 'frame-050',
    name: 'Thin Silver',
    color: '#c0c0c0',
    category: 'minimal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 6,
    texture: 'brushed-metal',
    secondaryColor: '#a8a8a8',
  },
  {
    id: 'frame-051',
    name: 'Thin Gold',
    color: '#c9a227',
    category: 'minimal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 6,
    texture: 'brushed-metal',
    secondaryColor: '#b89020',
    isPremium: true,
  },
  {
    id: 'frame-052',
    name: 'Thin Oak',
    color: '#b8956e',
    category: 'minimal',
    material: 'wood',
    profile: 'flat',
    defaultWidth: 10,
    texture: 'wood-grain',
    secondaryColor: '#a07850',
  },
  {
    id: 'frame-053',
    name: 'Hairline Black',
    color: '#1a1a1a',
    category: 'minimal',
    material: 'metal',
    profile: 'flat',
    defaultWidth: 4,
    texture: 'smooth',
  },
  {
    id: 'frame-054',
    name: 'Invisible',
    color: '#f5f5f5',
    category: 'minimal',
    material: 'composite',
    profile: 'flat',
    defaultWidth: 3,
    texture: 'smooth',
  },
];

/** Get frame color by ID */
export function getFrameColor(frameId: string): string {
  return FRAMES.find((f) => f.id === frameId)?.color ?? '#1a1a1a';
}

/** Get frame data by ID */
export function getFrameData(frameId: string): FrameData | undefined {
  return FRAMES.find((f) => f.id === frameId);
}

/** Generate SVG thumbnail for a frame */
export function createFrameThumbnail(
  frameColor: string,
  matColor: string = '#f5f5f5',
  frameWidth: number = 10,
): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
    <defs>
      <linearGradient id="fg${frameColor.replace('#', '')}" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style="stop-color:${frameColor};stop-opacity:1" />
        <stop offset="50%" style="stop-color:${frameColor};stop-opacity:0.9" />
        <stop offset="100%" style="stop-color:${frameColor};stop-opacity:0.7" />
      </linearGradient>
    </defs>
    <rect fill="url(#fg${frameColor.replace('#', '')})" width="100" height="100" rx="2"/>
    <rect fill="${matColor}" x="${frameWidth}" y="${frameWidth}" width="${100 - frameWidth * 2}" height="${100 - frameWidth * 2}"/>
    <rect fill="#8899aa" x="${frameWidth + 8}" y="${frameWidth + 8}" width="${100 - frameWidth * 2 - 16}" height="${100 - frameWidth * 2 - 16}" opacity="0.3"/>
    <rect fill="none" x="${frameWidth}" y="${frameWidth}" width="${100 - frameWidth * 2}" height="${100 - frameWidth * 2}" stroke="${frameColor}" stroke-width="1" opacity="0.5"/>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}
